import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  BookOpen,
  ChevronDown,
  ArrowRight,
  Table,
  Users,
  Package,
  UsersIcon,
  Building,
  Upload,
  Download,
  Edit,
  Trash2,
  CheckCircle,
  Settings,
  MessageSquare,
  Paperclip,
  AlertTriangle,
  CreditCard,
  Clock,
  Landmark,
  DollarSign,
  Star,
  Search,
  Eye,
  Shield,
  FileText,
  Layers
} from 'lucide-react';
import '../styles/Manual.css';

const Manual = () => {
  const { isSuperAdmin } = useSelector(state => state.auth);
  const [activeSection, setActiveSection] = useState(null);
  const [openSections, setOpenSections] = useState([]);
  const [themeColor, setThemeColor] = useState('#c4fffc');
  const mainContentRef = useRef(null);

  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
    const layoutContent = document.querySelector('.layout-content');
    if (layoutContent) {
      layoutContent.scrollTop = 0;
    }
  }, [activeSection]);

  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };
    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);
    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  const toggleSection = (sectionId) => {
    const isCurrentlyOpen = openSections.includes(sectionId);
    if (isCurrentlyOpen) {
      setOpenSections(openSections.filter(id => id !== sectionId));
    } else {
      setOpenSections([...openSections, sectionId]);
      setActiveSection(sectionId);
      setTimeout(() => {
        const element = document.getElementById(`section-content-${sectionId}`);
        if (element) {
          const layoutContent = document.querySelector('.layout-content');
          if (layoutContent) {
            const layoutTop = layoutContent.getBoundingClientRect().top;
            const scrollPosition = element.offsetTop - layoutTop - 100;
            layoutContent.scrollTo({ top: scrollPosition, behavior: 'smooth' });
          } else {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 350);
    }
  };

  const empresaActiva = (() => {
    try {
      const data = localStorage.getItem('empresaActiva');
      return data ? JSON.parse(data) : null;
    } catch { return null; }
  })();
  const userRole = empresaActiva?.rolUsuario || empresaActiva?.role;
  const userPermissions = empresaActiva?.permissions?.sections || empresaActiva?.permisos?.sections || {};
  const hasFullAccess = isSuperAdmin || userRole === 'admin' || userRole === 'editor';

  const allSections = [
    {
      id: 'maestro-supply',
      title: 'Maestro Supply',
      icon: Table,
      description: 'Matriz de órdenes, observaciones, documentos adjuntos y etapas',
      permission: 'maestro'
    },
    {
      id: 'clientes',
      title: 'Gestión de Clientes',
      icon: Users,
      description: 'Clientes, contactos, condiciones de pago y datos bancarios',
      permission: 'clientes'
    },
    {
      id: 'proveedores',
      title: 'Gestión de Proveedores',
      icon: Package,
      description: 'Proveedores, productos, calificaciones y condiciones comerciales',
      permission: 'proveedores'
    },
    {
      id: 'equipo',
      title: 'Gestión de Equipo',
      icon: UsersIcon,
      description: 'Usuarios, roles y permisos por sección',
      permission: 'equipo'
    },
    {
      id: 'configuracion',
      title: 'Configuración de Empresa',
      icon: Settings,
      description: 'Personalización de marca, colores, datos legales y contacto',
      permission: 'empresa'
    },
    {
      id: 'empresas',
      title: 'Panel Super Admin',
      icon: Building,
      description: 'Gestión multiempresa (Solo Super Admin)',
      superAdminOnly: true
    }
  ];

  const sections = allSections.filter(section => {
    if (section.superAdminOnly) return isSuperAdmin;
    if (hasFullAccess) return true;
    if (section.permission) return userPermissions[section.permission] === true;
    return true;
  });

  useEffect(() => {
    if (sections.length > 0 && (!activeSection || !sections.find(s => s.id === activeSection))) {
      setActiveSection(sections[0].id);
    }
  }, [sections.length]);

  const renderContent = (sectionId) => {
    if (sectionId === 'maestro-supply') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Maestro Supply - Matriz de Ordenes</h2>
            <p>
              El Maestro Supply es el corazon del sistema. Una matriz interactiva donde se
              registran todas las ordenes de compra y venta con vision completa del negocio.
            </p>
          </div>

          <div className="flujo-container">
            {/* Paso 1: Importar Excel */}
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Upload size={24} style={{color: themeColor}} />
                  <h3>Importar datos desde Excel</h3>
                </div>
                <p className="paso-description">
                  Haz clic en "Importar Excel" y selecciona tu archivo. El sistema detectara automaticamente las columnas.
                </p>
                <div className="paso-actions">
                  <h4>Columnas requeridas:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong>ITEM</strong> - Numero de item</li>
                    <li><CheckCircle size={16} /> <strong>CLIENTE</strong> - Nombre del cliente</li>
                    <li><CheckCircle size={16} /> <strong>DESCRIPCION</strong> - Descripcion del producto</li>
                    <li><CheckCircle size={16} /> <strong>CANTIDAD</strong> - Cantidad de productos</li>
                    <li><CheckCircle size={16} /> <strong>UNIDAD</strong> - Unidad de medida (UN, KG, MT, etc.)</li>
                    <li><CheckCircle size={16} /> <strong>P.U. $CLP</strong> - Precio unitario al cliente</li>
                    <li><CheckCircle size={16} /> <strong>PROVEEDOR</strong> - Nombre del proveedor</li>
                    <li><CheckCircle size={16} /> <strong>PU PROVEEDOR</strong> - Precio unitario del proveedor</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 2: Duplicados */}
            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Edit size={24} style={{color: themeColor}} />
                  <h3>Gestionar items duplicados</h3>
                </div>
                <p className="paso-description">
                  Si existen items ya registrados, el sistema mostrara opciones para manejar duplicados:
                </p>
                <div className="paso-actions">
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Sobrescribir</strong> - Actualizar registros existentes con los nuevos datos</li>
                    <li><CheckCircle size={16} /> <strong>Omitir</strong> - Mantener registros actuales sin cambios</li>
                    <li><CheckCircle size={16} /> <strong>Preguntar por cada uno</strong> - Tabla comparativa "Actual vs Nuevo" para decidir caso a caso</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 3: Mapeo */}
            <div className="flujo-paso">
              <div className="paso-number">3</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Users size={24} style={{color: themeColor}} />
                  <h3>Mapear clientes y proveedores</h3>
                </div>
                <p className="paso-description">
                  Si se detectan clientes o proveedores nuevos (no existentes en el sistema), podras:
                </p>
                <div className="paso-actions">
                  <ul>
                    <li><CheckCircle size={16} /> Crear un nuevo registro automaticamente</li>
                    <li><CheckCircle size={16} /> Mapear a un cliente/proveedor existente del sistema</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 4: Edicion en la matriz */}
            <div className="flujo-paso">
              <div className="paso-number">4</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Table size={24} style={{color: themeColor}} />
                  <h3>Edicion en la matriz</h3>
                </div>
                <p className="paso-description">
                  La matriz soporta dos modos de visualizacion: tabla (estilo Excel) y tarjetas (cards) para movil.
                </p>
                <div className="paso-actions">
                  <h4>Funcionalidades de edicion:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Doble clic</strong> en cualquier celda para editar en linea</li>
                    <li><CheckCircle size={16} /> <strong>Clic en el item</strong> para abrir modal de edicion completo con pestanas</li>
                    <li><CheckCircle size={16} /> <strong>Agregar filas</strong> con boton "+" (nueva fila vacia)</li>
                    <li><CheckCircle size={16} /> <strong>Eliminar filas</strong> seleccionando y usando el boton eliminar</li>
                    <li><CheckCircle size={16} /> <strong>Filtros por columna</strong> para buscar datos especificos</li>
                    <li><CheckCircle size={16} /> <strong>Exportar a Excel</strong> la matriz completa</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Calculos automaticos:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Total Venta = Cantidad x P.U. CLP</li>
                    <li><CheckCircle size={16} /> Total Costo = Cantidad x PU Proveedor</li>
                    <li><CheckCircle size={16} /> Margen = Total Venta - Total Costo</li>
                    <li><CheckCircle size={16} /> % Margen calculado automaticamente</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 5: Etapas */}
            <div className="flujo-paso">
              <div className="paso-number">5</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Layers size={24} style={{color: themeColor}} />
                  <h3>Sistema de etapas</h3>
                </div>
                <p className="paso-description">
                  Cada item puede avanzar por etapas que reflejan su estado en el proceso de compra/venta:
                </p>
                <div className="paso-actions">
                  <h4>Etapas disponibles:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Cotizacion</strong> - Etapa inicial, se esta cotizando</li>
                    <li><CheckCircle size={16} /> <strong>OC Emitida</strong> - Orden de compra emitida al proveedor</li>
                    <li><CheckCircle size={16} /> <strong>En Transito</strong> - Producto en camino</li>
                    <li><CheckCircle size={16} /> <strong>Recibido</strong> - Producto recibido en bodega</li>
                    <li><CheckCircle size={16} /> <strong>Entregado</strong> - Entregado al cliente</li>
                    <li><CheckCircle size={16} /> <strong>Facturado</strong> - Factura emitida</li>
                    <li><CheckCircle size={16} /> <strong>Pagado</strong> - Pago recibido del cliente</li>
                  </ul>
                  <p className="paso-description" style={{marginTop: '10px'}}>
                    Las etapas se muestran como barra de progreso visual en la parte superior de cada item.
                    Haz clic en una etapa para marcarla como completada.
                  </p>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 6: Observaciones */}
            <div className="flujo-paso">
              <div className="paso-number">6</div>
              <div className="paso-content">
                <div className="paso-header">
                  <MessageSquare size={24} style={{color: themeColor}} />
                  <h3>Observaciones y discusion</h3>
                </div>
                <p className="paso-description">
                  Cada item tiene un panel de observaciones tipo chat donde el equipo puede discutir,
                  adjuntar archivos y categorizar comentarios.
                </p>
                <div className="paso-actions">
                  <h4>Como usar:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Haz clic en la celda <strong>Observaciones</strong> de cualquier item para abrir el panel</li>
                    <li><CheckCircle size={16} /> Escribe tu comentario y presiona <strong>Enviar</strong></li>
                    <li><CheckCircle size={16} /> El badge junto a la celda muestra cuantas observaciones tiene cada item</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Categorias de observacion:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong style={{color: '#3b82f6'}}>Info</strong> - Informacion general</li>
                    <li><CheckCircle size={16} /> <strong style={{color: '#f59e0b'}}>Pendiente</strong> - Requiere seguimiento</li>
                    <li><CheckCircle size={16} /> <strong style={{color: '#ef4444'}}>Urgente</strong> - Atencion inmediata</li>
                    <li><CheckCircle size={16} /> <strong style={{color: '#8b5cf6'}}>Apreciacion</strong> - Comentario valorativo</li>
                    <li><CheckCircle size={16} /> <strong style={{color: '#10b981'}}>Resuelto</strong> - Tema cerrado</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Archivos adjuntos:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Adjunta imagenes, PDFs o documentos a cada observacion (max 5 archivos, 20MB c/u)</li>
                    <li><CheckCircle size={16} /> Preview en linea para imagenes y PDFs sin salir del sistema</li>
                    <li><CheckCircle size={16} /> Zoom y descarga directa desde el visor</li>
                    <li><CheckCircle size={16} /> Barra de progreso al subir archivos</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Acciones sobre observaciones:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Editar</strong> - Modifica el texto, categoria o elimina adjuntos de tus propias observaciones</li>
                    <li><CheckCircle size={16} /> <strong>Eliminar</strong> - Confirmacion en linea (sin alertas del navegador)</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 7: Documentos */}
            <div className="flujo-paso">
              <div className="paso-number">7</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Paperclip size={24} style={{color: themeColor}} />
                  <h3>Documentos adjuntos por item</h3>
                </div>
                <p className="paso-description">
                  Ademas de las observaciones, cada item puede tener documentos adjuntos directos
                  (ordenes de compra, facturas, guias de despacho, etc.).
                </p>
                <div className="paso-actions">
                  <ul>
                    <li><CheckCircle size={16} /> Columna con icono de clip (<Paperclip size={14} style={{verticalAlign: 'middle'}} />) en la matriz</li>
                    <li><CheckCircle size={16} /> Haz clic para abrir el panel de documentos del item</li>
                    <li><CheckCircle size={16} /> Sube archivos arrastrando o seleccionando</li>
                    <li><CheckCircle size={16} /> La fila debe estar guardada antes de adjuntar documentos</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 8: Datos incompletos */}
            <div className="flujo-paso">
              <div className="paso-number">8</div>
              <div className="paso-content">
                <div className="paso-header">
                  <AlertTriangle size={24} style={{color: themeColor}} />
                  <h3>Verificador de datos incompletos</h3>
                </div>
                <p className="paso-description">
                  Herramienta para identificar y navegar rapidamente por las celdas que faltan completar.
                </p>
                <div className="paso-actions">
                  <h4>Como usar:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Haz clic en <strong>"Datos Incompletos"</strong> en la barra superior</li>
                    <li><CheckCircle size={16} /> Aparece una barra amarilla con el conteo de celdas incompletas</li>
                    <li><CheckCircle size={16} /> Usa las <strong>flechas</strong> para navegar celda por celda</li>
                    <li><CheckCircle size={16} /> El sistema resalta la celda actual y hace scroll automatico</li>
                    <li><CheckCircle size={16} /> Un indicador flotante muestra que campos faltan en cada item</li>
                    <li><CheckCircle size={16} /> Presiona <strong>"Cerrar"</strong> para salir del modo de verificacion</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Campos verificados:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Cantidad</li>
                    <li><CheckCircle size={16} /> Precio unitario CLP (P.U. $CLP)</li>
                    <li><CheckCircle size={16} /> Precio unitario proveedor (PU Proveedor)</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 9: Cambios sin guardar */}
            <div className="flujo-paso">
              <div className="paso-number">9</div>
              <div className="paso-content">
                <div className="paso-header">
                  <AlertTriangle size={24} style={{color: themeColor}} />
                  <h3>Proteccion de cambios sin guardar</h3>
                </div>
                <p className="paso-description">
                  Si intentas salir del Maestro con cambios sin guardar, el sistema te mostrara
                  un aviso con los colores de tu tema personalizado.
                </p>
                <div className="paso-actions">
                  <ul>
                    <li><CheckCircle size={16} /> Se activa al hacer clic en cualquier enlace de navegacion</li>
                    <li><CheckCircle size={16} /> Tambien se activa con el boton "atras" del navegador</li>
                    <li><CheckCircle size={16} /> Opciones: <strong>Guardar y salir</strong>, <strong>Salir sin guardar</strong> o <strong>Cancelar</strong></li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 10: Exportar */}
            <div className="flujo-paso">
              <div className="paso-number">10</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Download size={24} style={{color: themeColor}} />
                  <h3>Exportar datos</h3>
                </div>
                <p className="paso-description">
                  Descarga la matriz completa como archivo Excel para reportes o respaldo.
                </p>
                <div className="paso-actions">
                  <ul>
                    <li><CheckCircle size={16} /> Boton <strong>"Exportar Excel"</strong> en la barra de herramientas</li>
                    <li><CheckCircle size={16} /> Incluye todas las columnas visibles con formato</li>
                    <li><CheckCircle size={16} /> Los calculos (totales, margenes) se exportan con sus valores</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sectionId === 'clientes') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Gestion de Clientes</h2>
            <p>
              Administra tu cartera de clientes con informacion comercial completa:
              datos de contacto, condiciones de pago, credito y datos bancarios.
            </p>
          </div>

          <div className="flujo-container">
            {/* Paso 1: Vista general */}
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Users size={24} style={{color: themeColor}} />
                  <h3>Panel de clientes</h3>
                </div>
                <p className="paso-description">
                  La vista principal muestra tarjetas con la informacion resumida de cada cliente
                  y estadisticas en la parte superior.
                </p>
                <div className="paso-actions">
                  <h4>Estadisticas del panel:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Clientes Activos</strong> - Total de clientes registrados</li>
                    <li><CheckCircle size={16} /> <strong>Pagan a Credito</strong> - Clientes con condicion de credito</li>
                    <li><CheckCircle size={16} /> <strong>Pagan Contado</strong> - Clientes con pago al contado</li>
                    <li><CheckCircle size={16} /> <strong>Ventas Historicas</strong> - Suma total de ventas a todos los clientes</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Busqueda:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Busca por nombre, RUT o rubro en la barra de busqueda</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 2: Crear cliente */}
            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Users size={24} style={{color: themeColor}} />
                  <h3>Crear un cliente nuevo</h3>
                </div>
                <p className="paso-description">
                  Haz clic en "Nuevo Cliente" para abrir el formulario. Tambien puedes crear clientes
                  directamente desde el Maestro al importar un Excel con clientes nuevos.
                </p>
                <div className="paso-actions">
                  <h4>Informacion basica:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Razon Social</strong> - Nombre legal de la empresa (obligatorio)</li>
                    <li><CheckCircle size={16} /> <strong>Nombre Comercial</strong> - Nombre de fantasia</li>
                    <li><CheckCircle size={16} /> <strong>RUT</strong> - Identificador fiscal (obligatorio)</li>
                    <li><CheckCircle size={16} /> <strong>Industria</strong> - Rubro del cliente (ej: Mineria, Construccion)</li>
                    <li><CheckCircle size={16} /> Direccion, ciudad, telefono, email, sitio web</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 3: Condiciones de pago */}
            <div className="flujo-paso">
              <div className="paso-number">3</div>
              <div className="paso-content">
                <div className="paso-header">
                  <CreditCard size={24} style={{color: themeColor}} />
                  <h3>Condiciones de pago</h3>
                </div>
                <p className="paso-description">
                  Configura las condiciones comerciales de cada cliente segun las practicas chilenas
                  y la Ley 21.131 de pago oportuno.
                </p>
                <div className="paso-actions">
                  <h4>Condicion de pago:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Contado</strong> - Pago inmediato contra factura</li>
                    <li><CheckCircle size={16} /> <strong>Credito</strong> - Pago diferido (30, 60, 90 o 120 dias)</li>
                    <li><CheckCircle size={16} /> <strong>Anticipo</strong> - Porcentaje de pago previo a la entrega</li>
                    <li><CheckCircle size={16} /> <strong>Contra Entrega</strong> - Pago al momento de recibir</li>
                    <li><CheckCircle size={16} /> <strong>Por Avance</strong> - Pago segun avance del proyecto</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Forma de pago:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Transferencia bancaria</li>
                    <li><CheckCircle size={16} /> Cheque</li>
                    <li><CheckCircle size={16} /> Letra de Cambio</li>
                    <li><CheckCircle size={16} /> Pagare</li>
                    <li><CheckCircle size={16} /> Factoring</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Campos de credito (cuando aplica):</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Dias de credito</strong> - 30, 60, 90 o 120 dias</li>
                    <li><CheckCircle size={16} /> <strong>Limite de credito</strong> - Monto maximo en CLP</li>
                    <li><CheckCircle size={16} /> <strong>% Anticipo</strong> - Porcentaje de pie (si aplica)</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 4: Datos bancarios */}
            <div className="flujo-paso">
              <div className="paso-number">4</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Landmark size={24} style={{color: themeColor}} />
                  <h3>Datos bancarios</h3>
                </div>
                <p className="paso-description">
                  Registra la informacion bancaria del cliente para gestionar pagos y transferencias.
                </p>
                <div className="paso-actions">
                  <h4>Informacion bancaria:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Banco</strong> - Banco de Chile, Estado, Santander, BCI, Itau, Scotiabank, BICE, Security, Falabella, Ripley, Consorcio</li>
                    <li><CheckCircle size={16} /> <strong>Tipo de cuenta</strong> - Corriente, Vista o Ahorro</li>
                    <li><CheckCircle size={16} /> <strong>Numero de cuenta</strong></li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 5: Contactos */}
            <div className="flujo-paso">
              <div className="paso-number">5</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Users size={24} style={{color: themeColor}} />
                  <h3>Gestion de contactos</h3>
                </div>
                <p className="paso-description">
                  Cada cliente puede tener multiples contactos. El contacto principal aparece
                  destacado en la tarjeta del cliente.
                </p>
                <div className="paso-actions">
                  <h4>Datos de cada contacto:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Nombre y apellido</li>
                    <li><CheckCircle size={16} /> Cargo en la empresa</li>
                    <li><CheckCircle size={16} /> Email y telefono</li>
                    <li><CheckCircle size={16} /> Marcar como contacto principal</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sectionId === 'proveedores') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Gestion de Proveedores</h2>
            <p>
              Administra tu red de proveedores con condiciones comerciales, calificaciones,
              catalogo de productos y datos bancarios completos.
            </p>
          </div>

          <div className="flujo-container">
            {/* Paso 1: Vista general */}
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Package size={24} style={{color: themeColor}} />
                  <h3>Panel de proveedores</h3>
                </div>
                <p className="paso-description">
                  Vista con tarjetas para cada proveedor y estadisticas resumidas.
                </p>
                <div className="paso-actions">
                  <h4>Estadisticas del panel:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Proveedores Activos</strong> - Total registrados</li>
                    <li><CheckCircle size={16} /> <strong>Con Credito</strong> - Proveedores que otorgan credito</li>
                    <li><CheckCircle size={16} /> <strong>Plazo Pago Prom.</strong> - Promedio de dias de pago</li>
                    <li><CheckCircle size={16} /> <strong>Calificacion Prom.</strong> - Promedio de rating (1 a 5 estrellas)</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 2: Crear proveedor */}
            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Package size={24} style={{color: themeColor}} />
                  <h3>Crear un proveedor nuevo</h3>
                </div>
                <p className="paso-description">
                  Haz clic en "Nuevo Proveedor" o crea uno automaticamente al importar un Excel
                  con proveedores no registrados.
                </p>
                <div className="paso-actions">
                  <h4>Informacion basica:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Razon Social y Nombre Comercial</li>
                    <li><CheckCircle size={16} /> RUT (obligatorio)</li>
                    <li><CheckCircle size={16} /> Industria/Rubro</li>
                    <li><CheckCircle size={16} /> Direccion, telefono, email, sitio web</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 3: Condiciones de pago */}
            <div className="flujo-paso">
              <div className="paso-number">3</div>
              <div className="paso-content">
                <div className="paso-header">
                  <CreditCard size={24} style={{color: themeColor}} />
                  <h3>Condiciones de pago</h3>
                </div>
                <p className="paso-description">
                  Define las condiciones comerciales acordadas con cada proveedor.
                </p>
                <div className="paso-actions">
                  <h4>Condicion de pago:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Contado</strong> - Pago inmediato</li>
                    <li><CheckCircle size={16} /> <strong>Credito</strong> - Pago diferido con plazo en dias</li>
                    <li><CheckCircle size={16} /> <strong>Anticipo</strong> - Porcentaje previo + saldo a plazo</li>
                    <li><CheckCircle size={16} /> <strong>Contra Entrega</strong> - Pago al recibir mercaderia</li>
                    <li><CheckCircle size={16} /> <strong>Por Avance</strong> - Pago segun progreso</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Forma de pago:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Transferencia, Cheque, Letra de Cambio, Pagare, Factoring</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Credito y limites:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Dias de credito (30/60/90/120)</li>
                    <li><CheckCircle size={16} /> Limite de credito en CLP</li>
                    <li><CheckCircle size={16} /> Porcentaje de anticipo (si aplica)</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 4: Datos bancarios */}
            <div className="flujo-paso">
              <div className="paso-number">4</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Landmark size={24} style={{color: themeColor}} />
                  <h3>Datos bancarios</h3>
                </div>
                <p className="paso-description">
                  Informacion bancaria del proveedor para realizar pagos.
                </p>
                <div className="paso-actions">
                  <ul>
                    <li><CheckCircle size={16} /> Banco (principales bancos chilenos)</li>
                    <li><CheckCircle size={16} /> Tipo de cuenta (Corriente, Vista, Ahorro)</li>
                    <li><CheckCircle size={16} /> Numero de cuenta</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 5: Calificacion */}
            <div className="flujo-paso">
              <div className="paso-number">5</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Star size={24} style={{color: themeColor}} />
                  <h3>Calificacion y evaluacion</h3>
                </div>
                <p className="paso-description">
                  Califica a tus proveedores de 1 a 5 estrellas para llevar un control de calidad.
                  Las estrellas se muestran en cada tarjeta de proveedor.
                </p>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            {/* Paso 6: Contactos y productos */}
            <div className="flujo-paso">
              <div className="paso-number">6</div>
              <div className="paso-content">
                <div className="paso-header">
                  <FileText size={24} style={{color: themeColor}} />
                  <h3>Contactos y productos/servicios</h3>
                </div>
                <p className="paso-description">
                  Gestiona los contactos clave y el catalogo de productos de cada proveedor.
                </p>
                <div className="paso-actions">
                  <h4>Contactos:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Nombre, cargo, email y telefono</li>
                    <li><CheckCircle size={16} /> Contacto principal destacado en la tarjeta</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Productos/Servicios:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Codigo del producto (ej: PRD-001)</li>
                    <li><CheckCircle size={16} /> Nombre y descripcion</li>
                    <li><CheckCircle size={16} /> Unidad de medida (UN, KG, MT, etc.)</li>
                    <li><CheckCircle size={16} /> Tiempo de entrega en dias</li>
                    <li><CheckCircle size={16} /> Precio Neto + IVA (calculado automaticamente)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sectionId === 'equipo') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Gestion de Equipo</h2>
            <p>
              Administra los usuarios del sistema, sus roles y permisos de acceso por seccion.
            </p>
          </div>

          <div className="flujo-container">
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Shield size={24} style={{color: themeColor}} />
                  <h3>Roles disponibles</h3>
                </div>
                <div className="paso-actions">
                  <h4>Super Admin:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Acceso total al sistema</li>
                    <li><CheckCircle size={16} /> Gestiona multiples empresas</li>
                    <li><CheckCircle size={16} /> Crea y elimina empresas</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Administrador:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Gestion completa de su empresa</li>
                    <li><CheckCircle size={16} /> Invita y gestiona usuarios</li>
                    <li><CheckCircle size={16} /> Configura datos de la empresa</li>
                    <li><CheckCircle size={16} /> Acceso a todas las secciones</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Editor:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Puede editar datos del Maestro</li>
                    <li><CheckCircle size={16} /> Acceso a clientes y proveedores</li>
                    <li><CheckCircle size={16} /> No puede eliminar datos ni gestionar equipo</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Miembro:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Acceso limitado segun permisos asignados</li>
                    <li><CheckCircle size={16} /> Solo ve las secciones habilitadas por un admin</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <UsersIcon size={24} style={{color: themeColor}} />
                  <h3>Invitar usuarios</h3>
                </div>
                <p className="paso-description">
                  Los administradores pueden invitar nuevos usuarios a la empresa.
                </p>
                <div className="paso-actions">
                  <h4>Proceso:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Ingresa nombre, apellido y email del nuevo usuario</li>
                    <li><CheckCircle size={16} /> Asigna un rol (Admin, Editor o Miembro)</li>
                    <li><CheckCircle size={16} /> Define una contrasena temporal</li>
                    <li><CheckCircle size={16} /> Si es Miembro, configura permisos por seccion</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">3</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Eye size={24} style={{color: themeColor}} />
                  <h3>Permisos por seccion</h3>
                </div>
                <p className="paso-description">
                  Para usuarios con rol "Miembro", puedes controlar el acceso a cada seccion individualmente.
                </p>
                <div className="paso-actions">
                  <h4>Secciones configurables:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Maestro Supply</li>
                    <li><CheckCircle size={16} /> Clientes</li>
                    <li><CheckCircle size={16} /> Proveedores</li>
                    <li><CheckCircle size={16} /> Equipo (solo lectura para miembros)</li>
                    <li><CheckCircle size={16} /> Configuracion de Empresa</li>
                  </ul>
                  <p className="paso-description" style={{marginTop: '10px'}}>
                    Este manual tambien se adapta: cada usuario solo ve las secciones a las que tiene acceso.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sectionId === 'configuracion') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Configuracion de Empresa</h2>
            <p>
              Personaliza la identidad visual, datos legales y de contacto de tu empresa.
              Solo Administradores tienen acceso a esta seccion.
            </p>
          </div>

          <div className="flujo-container">
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Settings size={24} style={{color: themeColor}} />
                  <h3>Logo y marca</h3>
                </div>
                <div className="paso-actions">
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Logo</strong> - Sube la imagen de tu empresa (se muestra en el sidebar)</li>
                    <li><CheckCircle size={16} /> <strong>Nombre Comercial</strong> - Nombre visible en la aplicacion</li>
                    <li><CheckCircle size={16} /> <strong>Descripcion</strong> - Breve descripcion de la empresa</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Settings size={24} style={{color: themeColor}} />
                  <h3>Colores personalizados</h3>
                </div>
                <p className="paso-description">
                  Personaliza los colores del sistema para que se ajusten a la identidad de tu empresa.
                </p>
                <div className="paso-actions">
                  <ul>
                    <li><CheckCircle size={16} /> <strong>Color primario</strong> - Botones, badges, acentos</li>
                    <li><CheckCircle size={16} /> <strong>Color secundario</strong> - Textos y bordes principales</li>
                    <li><CheckCircle size={16} /> Los colores se aplican en todo el sistema en tiempo real</li>
                    <li><CheckCircle size={16} /> Modales, alertas y botones respetan tu tema</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">3</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Building size={24} style={{color: themeColor}} />
                  <h3>Informacion legal</h3>
                </div>
                <div className="paso-actions">
                  <ul>
                    <li><CheckCircle size={16} /> Razon Social</li>
                    <li><CheckCircle size={16} /> RUT de la empresa</li>
                    <li><CheckCircle size={16} /> Giro Comercial</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">4</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Users size={24} style={{color: themeColor}} />
                  <h3>Contacto y equipo</h3>
                </div>
                <div className="paso-actions">
                  <h4>Datos de contacto:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Telefono, Email, Sitio Web, Direccion</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Vista de equipo:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Lista de administradores con email y rol</li>
                    <li><CheckCircle size={16} /> Contador de usuarios activos</li>
                    <li><CheckCircle size={16} /> Enlace rapido a la seccion Equipo</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (sectionId === 'empresas') {
      return (
        <div className="section-content">
          <div className="content-header">
            <h2>Panel Super Administrador</h2>
            <p>
              Disponible SOLO para Super Administradores. Gestion completa del
              sistema PAPER BOAT multiempresa.
            </p>
          </div>

          <div className="flujo-container">
            <div className="flujo-paso">
              <div className="paso-number">1</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Building size={24} style={{color: themeColor}} />
                  <h3>Vista del panel</h3>
                </div>
                <div className="paso-actions">
                  <h4>Metricas en tiempo real:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Total de empresas cliente registradas</li>
                    <li><CheckCircle size={16} /> Usuarios activos en todo el sistema</li>
                    <li><CheckCircle size={16} /> Pestanas: Empresas, Sistema, Actividad, Super Admins</li>
                  </ul>
                </div>
              </div>
              <div className="paso-arrow"><ArrowRight size={24} /></div>
            </div>

            <div className="flujo-paso">
              <div className="paso-number">2</div>
              <div className="paso-content">
                <div className="paso-header">
                  <Building size={24} style={{color: themeColor}} />
                  <h3>Gestion de empresas</h3>
                </div>
                <div className="paso-actions">
                  <h4>Por cada empresa:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Nombre Comercial y RUT</li>
                    <li><CheckCircle size={16} /> Estado (Activa/Inactiva)</li>
                    <li><CheckCircle size={16} /> Cantidad de usuarios y administradores</li>
                    <li><CheckCircle size={16} /> Acciones: Editar, Ver detalles, Desactivar</li>
                  </ul>
                  <h4 style={{marginTop: '15px'}}>Funcionalidades:</h4>
                  <ul>
                    <li><CheckCircle size={16} /> Crear nueva empresa con "Nueva Empresa"</li>
                    <li><CheckCircle size={16} /> Tabla con todas las empresas del sistema</li>
                    <li><CheckCircle size={16} /> Asignar usuarios a cada empresa</li>
                    <li><CheckCircle size={16} /> Estadisticas reales de usuarios y administradores</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="manual-container">
      <div className="manual-header">
        <div className="header-title">
          <BookOpen size={32} style={{color: '#000000'}} />
          <h1>Manual del Sistema</h1>
        </div>
        <p>Guia completa para el uso del sistema PAPER BOAT</p>
      </div>

      <div className="manual-content">
        <div className="manual-accordion">
          <h3>Secciones</h3>
          <div className="manual-nav">
            {sections.map((section) => (
              <div key={section.id} className="nav-section">
                <button
                  className={`section-toggle ${openSections.includes(section.id) ? 'active' : ''}`}
                  onClick={() => toggleSection(section.id)}
                  style={openSections.includes(section.id) ? {background: themeColor} : {}}
                >
                  <section.icon size={20} />
                  <span>{section.title}</span>
                  <ChevronDown
                    size={16}
                    className={`chevron ${openSections.includes(section.id) ? 'rotated' : ''}`}
                  />
                </button>
                <p className="section-desc">{section.description}</p>

                {openSections.includes(section.id) && (
                  <div id={`section-content-${section.id}`} className="section-content-expanded">
                    {renderContent(section.id)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Manual;
